import React, { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import Radio from "./Radio";
import {darken} from "polished";

interface Props {
    selected: boolean;
    onChange(): any;
    icon?: JSX.Element;
    label: string;
    label_xs?: string;
    separate?: boolean;
    conditionalContents?: any;
}

const RadioGroup = ({ selected, onChange, icon, label, label_xs, separate = false, conditionalContents }: Props) => {
    const themeContext = useContext(ThemeContext);

    return (
        <>
            <StyledRadioGroup tabIndex={0} className={'radio-group'} onClick={onChange} selected={selected} separate={separate} conditionalContentsVisible={selected && conditionalContents}>
                <Radio selected={selected} onChange={onChange}/>
                <span className={'radio-group__label'}>{label}</span>
                <span className={'radio-group__label radio-group__label--xs'}>{label_xs ? label_xs : label}</span>
                {icon ? (<div>{icon}</div>) : null}
            </StyledRadioGroup>
            {selected && conditionalContents && (
                <StyledConditionalContents>
                    { conditionalContents }
                </StyledConditionalContents>
            )}
        </>
    );
}

const StyledRadioGroup = styled.div<{selected: boolean, separate: boolean, conditionalContentsVisible: boolean}>`
    display: flex;
    align-items: center;
    margin: 1px;
    position: relative;
    cursor: pointer;
    border: 1px solid var(--layouts_container_border);
    gap: 12px;
    padding: 10px 16px;
    height: 56px;
    transition: background-color 0.1s linear;

    ${(props) => props.separate ? `
        margin-bottom: 12px;
        border-radius: var(--radius_xl);
    ` : `
        &:first-child {
            margin-bottom: 0;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
        }
        &:last-child {
            margin-top: 0;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
        }
    `}

    ${(props) => props.selected && !props.separate ? `
        background-color: var(--layouts_container_bg);
        border-color: var(--layouts_container_border);
        z-index: 10;

        .radio-group__icon {
            color: var(--layouts_container_border);
        }

        .radio-group__label {
            color: var(--text_body_color);
        }
    ` : `
        &:hover {
            background-color: var(--bg-grey);
        }
    `}

    ${(props) => props.conditionalContentsVisible ? `
        margin-bottom: 0;
        border-bottom-width: 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    ` : null}

    .radio-group {
        &__label {
            flex-grow: 1;
            font-weight: 500;
            font-size: 16px;
            line-height: 24px;
            display: none;
            @media screen and (min-width: 420px) {
                display: block;
            }
            &--xs {
                display: block;
                @media screen and (min-width: 420px) {
                    display: none;
                }
            }
        }
        &__icon {
            height: 24px;
            width: 24px;
            display: flex;
            justify-content: center;
            align-items: center;
        }
    }
`;

const StyledConditionalContents = styled.div`
    margin: 0 1px;
    padding: 2px 16px 16px 16px;
    border: 1px solid var(--layouts_container_border);
    border-top-width: 0;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
`;

export default RadioGroup;
